// 手机子页面：记录表按字段展示，比较表保留横向关系；扩大原生按钮触控区域，电脑布局及业务事件不变。
import { ResponsiveTable } from "../mobile/ResponsiveTable";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { Loader2, SearchCheck, CheckCircle2, XCircle } from "lucide-react";

const fmtAt = (iso) => { try { return new Date(iso).toLocaleString("zh-CN", { hour12: false }); } catch { return ""; } };

export function InvoiceNumberCheck({ api, zh }) {
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/invoice-number-check"); setR(data); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "复检失败" : "Check failed")); }
    setBusy(false);
  };
  const Row = ({ k, label, value, bad, testid }) => (
    <li className="flex items-center gap-2" data-testid={testid}><span className="text-slate-500">{label}:</span><b className={bad ? "text-red-700" : ""}>{value}</b></li>
  );
  return (
    <div className="mt-2 border-t border-slate-100 pt-2 space-y-2" data-testid="inv-check">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={run} disabled={busy} className="h-7 text-xs" data-testid="inv-check-run-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <SearchCheck className="h-3.5 w-3.5 mr-1" />}{zh ? "一键复检编号（只读）" : "Re-check numbering (read-only)"}
        </Button>
        <span className="text-[11px] text-slate-400">{zh ? "重跑重复号 / 空号 / 非标准格式 / 索引 / 计数器，不修改任何数据" : "Re-runs dupes / nulls / format / index / counter; changes nothing"}</span>
        {r && <span className="text-[11px] text-slate-400 ml-auto" data-testid="inv-check-at">{fmtAt(r.checkedAt)}</span>}
      </div>
      {r && (
        <div className="space-y-1.5" data-testid="inv-check-result" data-ok={r.canBuildUniqueIndex ? 1 : 0}>
          <div className={`flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold ${r.canBuildUniqueIndex ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="inv-check-verdict">
            {r.canBuildUniqueIndex ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
            {r.canBuildUniqueIndex
              ? (r.uniqueIndex ? (zh ? "唯一索引已建立，可放心依赖" : "Unique index present and safe") : (zh ? "唯一索引可成功建立（下次启动自动建）" : "Unique index can be built (next startup)"))
              : (zh ? `唯一索引无法建立：${r.reasons.map((x) => (x === "dupes" ? "存在重复编号" : "≥2 条显式空号")).join("、")} — 需人工处理后再依赖` : `Unique index cannot be built: ${r.reasons.join(", ")}`)}
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-1 text-slate-700">
            <Row label={zh ? "开票单总数" : "Invoices"} value={r.total} testid="inv-check-total" />
            <Row label={zh ? "最大标准编号" : "Max standard no."} value={r.maxStandardNumber != null ? `INV-CT-${String(r.maxStandardNumber).padStart(4, "0")}` : "—"} testid="inv-check-max" />
            <Row label={zh ? "计数器" : "Counter"} value={r.counter.initialized ? `${r.counter.seq}${r.counter.aheadOfMax ? "" : (zh ? "（低于最大编号！）" : " (below max!)")}` : (zh ? "未初始化（首次开票时按最大编号初始化）" : "not initialized")} bad={r.counter.initialized && !r.counter.aheadOfMax} testid="inv-check-counter" />
            <Row label={zh ? "重复编号" : "Duplicates"} value={r.duplicates.length} bad={r.duplicates.length > 0} testid="inv-check-dups" />
            <Row label={zh ? "显式空号" : "Null numbers"} value={r.nullCount} bad={r.nullCount > 1} testid="inv-check-nulls" />
            <Row label={zh ? "非标准格式" : "Non-standard"} value={r.nonStandardCount} bad={false} testid="inv-check-nonstd" />
            <Row label={zh ? "唯一索引" : "Unique index"} value={r.uniqueIndex ? (zh ? `已建立${r.indexSpec?.sparse ? "（sparse）" : ""}` : "yes") : (zh ? "未建立" : "no")} bad={!r.uniqueIndex} testid="inv-check-index" />
          </ul>
          {r.duplicates.length > 0 && (
            <div className="space-y-1" data-testid="inv-check-dup-list">
              {r.duplicates.map((x) => (
                <div key={x.invoiceNumber} className="rounded-lg border border-red-100 bg-red-50/50 px-2 py-1.5" data-testid={`inv-check-dup-${x.invoiceNumber}`}>
                  <p className="font-mono text-[11px] text-red-700 font-semibold">{x.invoiceNumber} × {x.count} <span className="font-sans font-normal text-slate-500">{zh ? "— 由财务人工处理（系统不自动改号）" : "— resolve manually"}</span></p>
                  <ResponsiveTable className="w-full text-[11px] mt-1"><tbody>
                    {(x.docs || []).map((doc, i) => (
                      <tr key={i} className="text-slate-700" data-testid={`inv-check-dup-doc-${x.invoiceNumber}-${i}`}>
                        <td className="pr-2">{doc.leadId ? <Link to={`/crm/leads/${doc.leadId}`} className="underline decoration-dotted">{doc.clientName || doc.leadId}</Link> : "—"}</td>
                        <td className="pr-2 text-slate-500">{doc.address || ""}</td>
                        <td className="pr-2">{doc.paymentKey}{doc.label ? ` · ${doc.label}` : ""}</td>
                        <td className="pr-2">{doc.status || ""}</td>
                        <td className="pr-2 text-right">{doc.amount != null ? `$${Number(doc.amount).toLocaleString()}` : ""}</td>
                        <td className="text-slate-400">{doc.createdAt ? String(doc.createdAt).slice(0, 10) : ""}</td>
                      </tr>
                    ))}
                  </tbody></ResponsiveTable>
                </div>
              ))}
            </div>
          )}
          {r.nonStandardCount > 0 && <p className="font-mono text-[11px] text-amber-700" data-testid="inv-check-nonstd-list">{zh ? "非标准：" : "Non-standard: "}{r.nonStandard.map((x) => x.invoiceNumber).join("  ")}{r.nonStandardCount > r.nonStandard.length ? " …" : ""}</p>}
        </div>
      )}
    </div>
  );
}
